import { useEffect, useState } from "react";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { signOut } from "@/lib/auth";
import { useTheme } from "@/providers/ThemeProvider";
import { Activity, Settings as SettingsIcon, ArrowLeft, LogOut, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Card } from "@/components/ui/card";
import { isNativeApp, getAppVersion } from "@/lib/platform";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AccountSettings from "@/components/AccountSettings";
import { AppLogo } from "@/components/AppLogo";
import { PenaltyManager } from "@/components/PenaltyManager";
import HealthKitSettings from "@/components/HealthKitSettings";
import { PreferencesSettings } from "@/components/PreferencesSettings";
import MobileBottomNav from "@/components/MobileBottomNav";
import { FooterLinksMenu } from "@/components/FooterLinksMenu";
import { MetricsTour } from "@/components/auth/mobile/MetricsTour";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { useAppSession } from "@/providers/AppSessionProvider";
import { ENABLE_COMMITMENT_PLEDGES } from "@/config/features";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme } = useTheme();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [metricsTourOpen, setMetricsTourOpen] = useState(false);
  const [appVersion, setAppVersion] = useState<string>("1.0.1");
  const [accountExpanded, setAccountExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Use centralized session management
  const { ready, session, isAuthenticated } = useAppSession();
  const user = session?.user || null;

  useEffect(() => {
    // Fetch the app version
    getAppVersion().then(setAppVersion);
  }, []);

  // Check if we should expand the Account section
  useEffect(() => {
    if (location.state?.expandAccount) {
      setAccountExpanded(true);
      // Clear the state after using it
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Redirect to auth if not authenticated (only after session is ready)
  useEffect(() => {
    if (ready !== 'ready') return;

    if (!isAuthenticated) {
      console.log("[Settings] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);

  // Get profile data (includes premium status and username)
  const { data: profile, isLoading: isLoadingProfile } = useProfile(user?.id);

  // Use centralized subscription status - this ensures status persists from Upgrade page
  const { isPremium, isLoading: isLoadingSubscription } = useSubscriptionStatus(user?.id);

  // Only redirect if profile is loaded and has no username
  useEffect(() => {
    if (ready !== 'ready' || !user || isLoadingProfile) return;

    // IMPORTANT: Only redirect if we have a loaded profile that definitely has no username
    // If profile is undefined or null, it might just be loading/refetching - don't redirect
    if (profile && profile.username === null) {
      console.log("[Settings] No username found (username is null), redirecting to auth for onboarding");
      localStorage.removeItem("onboarding_complete");
      navigate("/auth", { replace: true });
    }
  }, [ready, user, profile, isLoadingProfile, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const handleSignOutClick = () => {
    if (isMobile) {
      setShowSignOutDialog(true);
    } else {
      handleSignOut();
    }
  };

  if (ready === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <div className="container max-w-6xl mx-auto py-8 px-4">
        <header className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <AppLogo onClick={() => navigate("/")} />
            <div className="flex gap-2">
              {isMobile ? (
                <>
                  <FooterLinksMenu onOpenMetricsTour={() => setMetricsTourOpen(true)} />
                  <Button variant="ghost" size="sm" onClick={toggleTheme} title={t('accessibility.labels.toggle_theme')}>
                    <Moon className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSignOutClick}>
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('settings.back_dashboard')}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={toggleTheme} title={t('dashboard.toggle_theme')}>
                    <Moon className="w-5 h-5" />
                  </Button>
                  <Button variant="outline" onClick={handleSignOutClick}>{t('settings.sign_out')}</Button>
                </>
              )}
            </div>
          </div>
        </header>

        {!isMobile && (
          <div className="mb-6 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6" />
            <h2 className="text-2xl font-bold">{t('settings.title')}</h2>
          </div>
        )}

        <div className="space-y-4">
          {/* Preferences */}
          <Collapsible defaultOpen={false}>
            <Card className="border-border/50">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚙️</span>
                  <h3 className="text-lg font-semibold">{t('settings.preferences.title')}</h3>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300">
                  <PreferencesSettings />
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Commitment Pledges */}
          {ENABLE_COMMITMENT_PLEDGES && (
            <Collapsible defaultOpen={false}>
              <Card className="border-border/50">
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💰</span>
                    <h3 className="text-lg font-semibold">{t('settings.commitment_pledges.title')}</h3>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300">
                    <PenaltyManager />
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {/* HealthKit Integration */}
          <Collapsible defaultOpen={false}>
            <Card className="border-border/50">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🍎</span>
                  <h3 className="text-lg font-semibold">{t('settings.healthkit.title')}</h3>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300">
                  <HealthKitSettings userId={user.id} />
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Profile */}
          <Collapsible defaultOpen={false}>
            <Card className="border-border/50">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👤</span>
                  <h3 className="text-lg font-semibold">{t('settings.profile.title')}</h3>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300">
                  <AccountSettings
                    userId={user.id}
                    isPremium={isPremium}
                    isLoadingPremium={isLoadingSubscription || isLoadingProfile}
                    showOnlyProfile={true}
                  />
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Account */}
          <Collapsible open={accountExpanded} onOpenChange={setAccountExpanded}>
            <Card className="border-border/50">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔐</span>
                  <h3 className="text-lg font-semibold">{t('settings.account.title')}</h3>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300">
                  <AccountSettings
                    userId={user.id}
                    isPremium={isPremium}
                    isLoadingPremium={isLoadingSubscription || isLoadingProfile}
                    showOnlyAccount={true}
                  />
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* App Version */}
        <div className="mt-8 pb-4 text-center">
          <p className="text-xs text-muted-foreground/50">
            v{appVersion}
          </p>
        </div>
      </div>
      <MobileBottomNav />

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.sign_out')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.sign_out_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>{t('settings.sign_out')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Metrics Tour */}
      <MetricsTour
        isOpen={metricsTourOpen}
        onClose={() => setMetricsTourOpen(false)}
      />
    </div>
  );
};

export default Settings;
